import React, { useState } from 'react';
import { Search, X, FileText } from 'lucide-react';

import API_BASE_URL from "../config";

const POInvoiceTransmittal = () => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getUserId = () => {
    const user = localStorage.getItem("user");
    if (user) {
      const userData = JSON.parse(user);
      return userData.userId || '';
    }
    return '';
  };

  const [transmittalForm, setTransmittalForm] = useState({
    transmittalDate: getTodayDate(),
    encodedBy: getUserId()
  });


  const openSearchModal = async () => {
    setShowSearchModal(true);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/T_PO_Invoice_Details`);
      const data = await res.json();
      setAllInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Error loading invoice list');
    } finally {
      setLoading(false);
    }
  };

  const selectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceNumber(invoice.invoiceNo);
    setShowSearchModal(false);
    setSearchTerm('');
    setTransmittalForm({
      transmittalDate: getTodayDate(),
      encodedBy: getUserId()
    });
  };

  const filteredInvoices = allInvoices.filter(inv => 
    inv.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateTransmittal = async () => {
    if (!transmittalForm.transmittalDate || !transmittalForm.encodedBy) {
      alert('Please fill all transmittal fields');
      return;
    }

    try {
      const payload = {
        ...selectedInvoice,
        invoiceTransmittalDate: new Date(transmittalForm.transmittalDate).toISOString(),
        transmittalEncodedBy: parseInt(transmittalForm.encodedBy),
        transmittalEncodedDate: new Date().toISOString()
      };

      const res = await fetch(`${API_BASE_URL}api/T_PO_Invoice_Details/${selectedInvoice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('Transmittal updated successfully!');
        setTransmittalForm({ transmittalDate: getTodayDate(), encodedBy: getUserId() });
        setSelectedInvoice(null);
        setInvoiceNumber('');
      }
    } catch (error) {
      console.error('Error updating transmittal:', error);
      alert('Error updating transmittal');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="min-h-screen  from-blue-50 via-slate-50 to-blue-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 shadow-lg mb-6 backdrop-blur-sm bg-opacity-95">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7" />
            PO Invoice Transmittal  
          </h1>
        </div>

        {/* Search Section */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-6 mb-6 border border-white/20">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Search Invoice Number:
              </label>
              <input
                type="text"
                value={invoiceNumber}
                readOnly
                placeholder="Click search button to find an invoice"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button
              onClick={openSearchModal}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <Search className="w-5 h-5" />
              Search Invoice
            </button>
          </div>
        </div>

        {/* Search Invoice Modal */}
        {showSearchModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-white/20">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Search Invoice</h3>
                <button 
                  onClick={() => {
                    setShowSearchModal(false);
                    setSearchTerm('');
                  }} 
                  className="hover:bg-white/20 rounded-lg p-1.5 transition-all"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              <div className="p-6">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Search Invoice Number
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                  autoFocus
                />
              </div>

              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="text-gray-500 mt-4 text-sm font-medium">Loading invoice list...</p>
                  </div>
                ) : filteredInvoices.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm font-medium">No invoices found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredInvoices.map((inv) => (
                      <button
                        key={inv.id}
                        onClick={() => selectInvoice(inv)}
                        className="w-full text-left px-5 py-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-all backdrop-blur-sm bg-white/50"
                      >
                        <div className="font-semibold text-blue-700 mb-2 text-sm">
                          Invoice No: {inv.invoiceNo}
                        </div>
                        <div className="text-xs text-gray-600 space-x-4">
                          <span><span className="font-semibold">Amount:</span> ₱{parseFloat(inv.invoiceAmount).toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                          <span>•</span>
                          <span><span className="font-semibold">Date:</span> {formatDate(inv.invoiceDate)}</span>
                          <span>•</span>
                          <span><span className="font-semibold">PO Header ID:</span> {inv.poHeaderId}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transmittal Form */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-6 border border-white/20">
          <div className="space-y-5">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-red-600 text-sm font-semibold mb-3">Please Set Invoice Transmittal Date</p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-semibold text-gray-700 w-32">Transmittal Date:</label>
                  <input
                    type="date"
                    value={transmittalForm.transmittalDate}
                    onChange={(e) => setTransmittalForm({...transmittalForm, transmittalDate: e.target.value})}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-red-600 text-sm font-semibold mb-3">Please Input Invoice Number</p>
              
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-semibold text-gray-700 w-32">Invoice Number:</label>
                  <input
                    type="text"
                    value={selectedInvoice ? selectedInvoice.invoiceNo : ''}
                    readOnly
                    placeholder="Search and select an invoice first"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-700 mb-3 text-sm">Verify Invoice Details</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Invoice Amount</label>
                  <input
                    type="text"
                    value={selectedInvoice ? parseFloat(selectedInvoice.invoiceAmount).toLocaleString('en-US', {minimumFractionDigits: 2}) : ''}
                    readOnly
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Ref. PO Number</label>
                  <input
                    type="text"
                    value={selectedInvoice ? `PO-${selectedInvoice.poHeaderId}` : ''}
                    readOnly
                    placeholder="PO-"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Encoded By (User ID)</label>
                <input
                  type="number"
                  value={transmittalForm.encodedBy}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm"
                />
              </div>
            </div>
            
            <button
              onClick={updateTransmittal}
              disabled={!selectedInvoice}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg font-medium flex items-center justify-center gap-3 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-5 h-5" />
              Save Transmittal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POInvoiceTransmittal;